/**
 * Everything brand- or behaviour-shaped lives here. Changing Jeff's copy, his
 * colours, the side his bowls sit on, or how long he deliberates should never
 * require touching a component.
 */

import type { Direction } from '../../shared/types.ts';

export type BowlSide = 'left' | 'right';

export const JEFF = {
  // ── Identity ──────────────────────────────────────────────────────────────
  /**
   * The name, which is the domain. They were "JEFF.TRADE" against
   * jeffthechicken.trade for a while — jeff.trade itself costs $350 — and a
   * wordmark that does not match the address bar is just a second brand nobody
   * asked for.
   */
  appName: 'JEFFTHECHICKEN.TRADE',
  /** Rendered as two tokens so the logo can style them independently. */
  appNameParts: ['JEFFTHECHICKEN', '.TRADE'] as const,
  tagline: 'MARKETS. DECIDED.',
  jeffVersion: 'JEFF-1.0',
  publicOrigin: 'https://jeffthechicken.trade',
  /** Bare host, for anywhere the domain is displayed rather than linked. */
  publicDomain: 'jeffthechicken.trade',

  // ── The call ──────────────────────────────────────────────────────────────
  longLabel: 'LONG',
  shortLabel: 'SHORT',
  /**
   * Read directly by the SVG pen, which needs literal values rather than CSS
   * variables. They are mirrored as `--long` / `--short` in
   * `src/styles/base.css`, which is also where the rest of the palette lives —
   * change both together.
   */
  // Read by the 3D scene for the bowls (see buildPen.ts). Deliberately not the
  // same as --long/--short in base.css: those are text on cream and need more
  // contrast than a lit object in daylight does.
  longColour: '#24c46f',
  shortColour: '#e03a4c',

  /**
   * Which side of the pen the green bowl sits on, viewed from the camera.
   * Flip to 'right' if reference footage shows the opposite arrangement — the
   * pen, the bowls, the labels and Jeff's pathing all follow this one value.
   */
  longBowlSide: 'left' as BowlSide,

  /**
   * Probability Jeff goes long, in [0, 1]. Ships at exactly 0.5, and nothing in
   * the UI reads or writes it — a control here would turn a coin flip into
   * something that looks like a forecast. See `src/lib/random.ts` for how it is
   * applied.
   */
  longProbability: 0.5,

  // ── Timing ────────────────────────────────────────────────────────────────
  countdownSeconds: 3,
  /**
   * How long Jeff deliberates. A minute is the hard ceiling — `planPath`
   * clamps to `decisionHardCapMs` no matter what these are set to, so a
   * mis-edit here can never leave someone watching a chicken indefinitely.
   * The upper bound sits below the cap because 45 seconds of wandering is
   * already a long time to hold an audience.
   */
  decisionMinMs: 10_000,
  decisionMaxMs: 45_000,
  decisionHardCapMs: 60_000,
  /** How long the final commitment walk towards the chosen bowl takes. */
  commitMs: 2_200,
  /** Beat between Jeff reaching the bowl and the result panel appearing. */
  revealDelayMs: 900,
  /** Reduced-motion path: a single short, direct transition instead. */
  reducedMotionMs: 2_400,

  // ── Features ──────────────────────────────────────────────────────────────
  /** Audio is architected but silent until assets are dropped in. */
  enableAudio: false,
  /** Local history on the record page and the "recent calls" strip. */
  enableHistory: true,
  /**
   * Attempt to persist calls through the Worker. When false, or when the Worker
   * reports no database, calls live in localStorage and sharing still works
   * within this browser.
   */
  enableD1: true,
  enableEasterEggs: true,
  /**
   * Whether Jeff names a market.
   *
   * Off puts him back to bare LONG/SHORT calls with nothing to resolve against,
   * which is the state the site shipped in. On means each call draws a ticker
   * from `shared/assets.ts` — cosmetically, from the seeded stream, so the
   * outcome is still the only thing the CSPRNG decides.
   */
  enableAssets: true,
  /**
   * A `.glb` for Jeff, or null for the hand-built chicken in `buildJeff.ts`.
   *
   * The current file is Quaternius's CC0 "Chicken" — see
   * `public/models/CREDITS.txt`. Loading is async and failure is non-fatal:
   * if the fetch or parse fails, the procedural bird is already in the scene
   * and simply stays there.
   */
  /**
   * Optional `.glb` to use instead of the hand-built chicken.
   *
   * Null on purpose. The obvious CC0 candidate (Quaternius' "Chicken") was tried
   * and reverted: its armature has four joints — Body, Head, Head2, Head3 — and
   * no legs at all, so its Walk clip is a body bob with the feet welded to the
   * floor. `buildJeff.ts` has hips and knees and a gait that keeps the head level
   * over them, which is the thing that actually reads as a chicken.
   *
   * So the bar for replacing him is not polygon count: the rig must have leg
   * bones. Drop a file in `public/models/`, point this at it, and record the
   * licence alongside it — `loadJeffModel.ts` handles scale, orientation and
   * grounding on its own.
   */
  modelUrl: null as string | null,

  // ── Legal-ish ─────────────────────────────────────────────────────────────
  disclaimer: 'Jeff is a chicken. This is not financial advice.',
} as const;

export const DIRECTION_META: Record<Direction, { label: string; verdict: string }> = {
  LONG: { label: JEFF.longLabel, verdict: 'JEFF HAS GONE LONG' },
  SHORT: { label: JEFF.shortLabel, verdict: 'JEFF HAS GONE SHORT' },
};

/** Which physical side each direction's bowl occupies, honouring `longBowlSide`. */
export function bowlSideFor(direction: Direction): BowlSide {
  if (direction === 'LONG') return JEFF.longBowlSide;
  return JEFF.longBowlSide === 'left' ? 'right' : 'left';
}

/** Headline variants for the result panel. Picked per call. */
export const RESULT_HEADLINES = [
  'OFFICIAL JEFF CALL',
  'THE ORACLE HAS SPOKEN',
  'JEFF SEES SOMETHING',
  'INSTITUTIONAL-GRADE POULTRY ANALYSIS',
  'SIGNAL CONFIRMED',
] as const;

/** Burnt into the corner of the shot by `CameraHud`. */
export const OVERLAY_LABELS = {
  live: 'LIVE',
  camera: 'JEFF CAM 01',
} as const;

